import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Eyebrow, MaskLines, Reveal } from "../motion/Reveal";
import { getIcon } from "../../lib/icons";
import { useSite } from "../../lib/site";
import { scrollToId } from "../../lib/scroll";

const PATTERN = [2, 1, 1, 2, 2, 1];
const LG_SPAN = { 1: "lg:col-span-1", 2: "lg:col-span-2", 3: "lg:col-span-3" };
const CHIP = ["bg-leaf text-ink", "bg-sun text-ink", "bg-azure text-white", "bg-coral text-white", "bg-navy text-white", "bg-leaf-soft text-ink"];

export function bentoSpans(n) {
  const spans = [];
  let col = 0;
  for (let i = 0; i < n; i += 1) {
    let w = PATTERN[i % PATTERN.length];
    if (col + w > 3) w = 3 - col;
    spans.push(w);
    col = (col + w) % 3;
  }
  if (col !== 0 && n) spans[n - 1] += 3 - col;
  return spans;
}

const Price = ({ service, copy, dark }) => (
  <span className={`font-mono text-xs uppercase tracking-[0.1em] ${dark ? "text-white/85" : "text-ink/70"}`} data-testid="service-price">
    {service.price ? `${copy.price_prefix} ${service.price}${service.price_note ? ` · ${service.price_note}` : ""}` : copy.price_empty}
  </span>
);

const ServiceCard = ({ service, index, span, image, copy, mdFull }) => {
  const Icon = getIcon(service.icon);
  const wide = span >= 2;
  const img = service.image || (wide ? image : "");
  const dark = Boolean(img);
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
      className={`group relative flex min-h-[280px] flex-col overflow-hidden rounded-[1.75rem] border p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(10,18,48,.45)] sm:p-8 ${
        dark ? "border-transparent text-white" : "border-ink/10 bg-white text-ink"
      } ${mdFull ? "md:col-span-2" : ""} ${LG_SPAN[span]}`}
      data-testid={`service-card-${index}`}
    >
      {dark && (
        <>
          <img src={img} alt={service.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-tr from-ink via-ink/75 to-ink/10" />
        </>
      )}
      <div className="relative flex items-start justify-between">
        <span className={`grid h-14 w-14 place-items-center rounded-2xl ${CHIP[index % CHIP.length]}`}>
          <Icon className="h-6 w-6" strokeWidth={1.8} />
        </span>
        <span className="font-mono text-xs opacity-60">{String(index + 1).padStart(2, "0")}</span>
      </div>
      <div className="relative mt-auto pt-12">
        <h3 className={`font-display font-semibold leading-[1.02] tracking-[-0.03em] ${wide ? "text-3xl sm:text-4xl" : "text-2xl sm:text-[1.7rem]"}`}>{service.title}</h3>
        <p className={`mt-3 max-w-md text-base leading-relaxed ${dark ? "text-white/85" : "text-ink/75"}`}>{service.description}</p>
        <div className={`mt-6 flex items-center justify-between border-t pt-4 ${dark ? "border-white/20" : "border-ink/10"}`}>
          <Price service={service} copy={copy} dark={dark} />
          <ArrowUpRight className="h-5 w-5 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </div>
      </div>
    </motion.article>
  );
};

const Plans = ({ plans, copy }) => (
  <div className="mt-24" data-testid="plans-section">
    <h3 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">{copy.plans_title}</h3>
    <p className="mt-2 text-base text-ink/70">{copy.plans_text}</p>
    <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {plans.map((p, i) => (
        <Reveal
          key={p.id}
          delay={i * 0.06}
          className={`flex flex-col rounded-[1.75rem] p-8 ${p.highlighted ? "bg-ink text-white" : "border border-ink/10 bg-white"}`}
          data-testid={`plan-card-${i}`}
        >
          <p className="font-mono text-xs uppercase tracking-[0.14em] opacity-75">{p.name}</p>
          <p className="mt-4 font-display text-4xl font-semibold tracking-tight">
            {p.price || "—"} <span className="text-base font-normal opacity-70">{p.period}</span>
          </p>
          {p.description && <p className="mt-3 text-base opacity-80">{p.description}</p>}
          <ul className="mt-6 space-y-2.5">
            {(p.features || []).map((f, j) => (
              <li key={j} className="flex items-start gap-2.5 text-sm">
                <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.highlighted ? "text-leaf" : "text-leaf-deep"}`} /> {f}
              </li>
            ))}
          </ul>
        </Reveal>
      ))}
    </div>
  </div>
);

export const Solutions = () => {
  const { content, services, plans } = useSite();
  const copy = content.soluciones;
  const spans = bentoSpans(services.length);
  const images = content.media.soluciones_images || [];
  let wideCount = 0;
  return (
    <section id="soluciones" className="relative bg-paper py-24 sm:py-32 lg:py-40" data-testid="soluciones-section">
      <div className="container-x">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Eyebrow tone="light">{copy.eyebrow}</Eyebrow>
            <MaskLines lines={copy.title_lines} className="h-section mt-6" testId="soluciones-title" />
          </div>
          <Reveal className="lg:col-span-5">
            <p className="text-lg leading-relaxed text-ink/80">{copy.text}</p>
            <button onClick={() => scrollToId("registro")} className="btn-ink group mt-6" data-testid="soluciones-cta">
              Quiero ser distribuidor <ArrowUpRight className="btn-arrow h-5 w-5" />
            </button>
          </Reveal>
        </div>
        {services.length > 0 && (
          <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-5" data-testid="services-bento">
            {services.map((s, i) => {
              const img = spans[i] >= 2 ? images[wideCount++ % Math.max(images.length, 1)] : "";
              return <ServiceCard key={s.id} service={s} index={i} span={spans[i]} image={img} copy={copy} mdFull={i === services.length - 1 && services.length % 2 === 1} />;
            })}
          </div>
        )}
        {plans.length > 0 && <Plans plans={plans} copy={copy} />}
      </div>
    </section>
  );
};
